"use client";

import type { Coupon } from "@/types/prisma";
import { Loader2, Plus, Search, Tag, Trash2, ToggleLeft, ToggleRight, X } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { AdminPagination } from "@/components/admin/admin-pagination";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/lib/utils";

export function CouponManager({ initialCoupons }: { initialCoupons: Coupon[] }) {
  const [coupons, setCoupons] = useState(initialCoupons);
  useEffect(() => setCoupons(initialCoupons), [initialCoupons]);
  const [loading, setLoading] = useState(false);
  const [discountType, setDiscountType] = useState<"pct" | "amt">("pct");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return coupons;
    return coupons.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        (c.description && c.description.toLowerCase().includes(q))
    );
  }, [coupons, search]);

  const paginated = useMemo(() => {
    return filtered.slice((page - 1) * perPage, page * perPage);
  }, [filtered, page, perPage]);


  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    const fd = new FormData(event.currentTarget);
    const payload = {
      code: String(fd.get("code")).trim().toUpperCase(),
      description: String(fd.get("description")),
      discountPct: discountType === "pct" ? Number(fd.get("discountValue")) : undefined,
      discountAmt: discountType === "amt" ? Number(fd.get("discountValue")) : undefined,
      minOrder: Number(fd.get("minOrder") ?? 0),
      maxUses: fd.get("maxUses") ? Number(fd.get("maxUses")) : undefined,
      expiresAt: fd.get("expiresAt") ? String(fd.get("expiresAt")) : undefined,
      isActive: true
    };

    try {
      const response = await fetch("/api/admin/coupons", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.message ?? "Unable to create coupon.");
      setCoupons((prev) => [data.coupon, ...prev]);
      (event.target as HTMLFormElement).reset();
      toast.success("Coupon created");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to create coupon.");
    } finally {
      setLoading(false);
    }
  }

  async function toggleActive(coupon: Coupon) {
    const prev = coupons;
    setCoupons((list) =>
      list.map((c) => (c.id === coupon.id ? { ...c, isActive: !c.isActive } : c))
    );
    try {
      const response = await fetch(`/api/admin/coupons/${coupon.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !coupon.isActive })
      });
      if (!response.ok) throw new Error("Unable to update coupon.");
      toast.success(coupon.isActive ? "Coupon deactivated" : "Coupon activated");
    } catch (error) {
      setCoupons(prev);
      toast.error(error instanceof Error ? error.message : "Unable to update coupon.");
    }
  }

  async function deleteCoupon(id: string) {
    const prev = coupons;
    setCoupons((list) => list.filter((c) => c.id !== id));
    try {
      const response = await fetch(`/api/admin/coupons/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error("Unable to delete coupon.");
      toast.success("Coupon deleted");
    } catch (error) {
      setCoupons(prev);
      toast.error(error instanceof Error ? error.message : "Unable to delete coupon.");
    }
  }

  return (
    <div className="space-y-6">
      {/* Create form */}
      <form
        onSubmit={onSubmit}
        className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900"
      >
        <h2 className="mb-4 text-xl font-black text-slate-950 dark:text-white">Create coupon</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <input
            name="code"
            required
            placeholder="Code (e.g. SAVE10)"
            className="h-11 rounded-md border border-slate-200 bg-transparent px-3 uppercase outline-none focus:border-amazon-orange dark:border-white/10"
          />
          <input
            name="description"
            required
            placeholder="Description"
            className="h-11 rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10"
          />
          <div className="flex gap-2">
            <select
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value as "pct" | "amt")}
              className="h-11 rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10"
            >
              <option value="pct">% Discount</option>
              <option value="amt">Fixed Amount</option>
            </select>
            <input
              name="discountValue"
              type="number"
              required
              step="0.01"
              min="1"
              placeholder={discountType === "pct" ? "e.g. 10" : "e.g. 500"}
              className="h-11 flex-1 min-w-0 rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10"
            />
          </div>
          <input
            name="minOrder"
            type="number"
            step="0.01"
            min="0"
            placeholder="Min. order (0 = no minimum)"
            defaultValue={0}
            className="h-11 rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10"
          />
          <input
            name="maxUses"
            type="number"
            min="1"
            placeholder="Max uses (blank = unlimited)"
            className="h-11 rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10"
          />
          <input
            name="expiresAt"
            type="datetime-local"
            className="h-11 rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10"
          />
        </div>
        <Button type="submit" className="mt-5" disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create coupon
        </Button>
      </form>

      {/* List */}
      {coupons.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
          <Tag className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 font-bold">No coupons yet</p>
          <p className="mt-1 text-sm text-slate-500">Create your first coupon above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search coupons by code or description..."
                className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-8 py-2 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium">
                Total: <strong className="text-slate-900 dark:text-white font-bold">{coupons.length}</strong> coupons
              </span>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-left text-sm">
                <thead className="bg-slate-100 text-xs uppercase text-slate-500 dark:bg-white/10">
                  <tr>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">Discount</th>
                    <th className="px-4 py-3">Min. Order</th>
                    <th className="px-4 py-3">Used</th>
                    <th className="px-4 py-3">Expires</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-slate-500">
                        No coupons found matching &quot;{search}&quot;.
                      </td>
                    </tr>
                  ) : (
                    paginated.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-white/5">
                        <td className="px-4 py-4 font-black uppercase">{c.code}</td>
                        <td className="px-4 py-4">{c.description}</td>
                        <td className="px-4 py-4 font-bold">
                          {c.discountPct ? `${c.discountPct}%` : formatPrice(c.discountAmt ?? 0)}
                        </td>
                        <td className="px-4 py-4">{formatPrice(c.minOrder)}</td>
                        <td className="px-4 py-4">
                          {c.usedCount}
                          {c.maxUses ? ` / ${c.maxUses}` : ""}
                        </td>
                        <td className="px-4 py-4 text-xs">
                          {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : "Never"}
                        </td>
                        <td className="px-4 py-4">
                          <Badge tone={c.isActive ? "success" : "default"}>
                            {c.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => toggleActive(c)}
                              className="rounded-md p-2 hover:bg-slate-100 dark:hover:bg-white/10"
                              aria-label={c.isActive ? "Deactivate coupon" : "Activate coupon"}
                              title={c.isActive ? "Deactivate" : "Activate"}
                            >
                              {c.isActive ? (
                                <ToggleRight className="h-4 w-4 text-amazon-green" />
                              ) : (
                                <ToggleLeft className="h-4 w-4 text-slate-400" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteCoupon(c.id)}
                              className="rounded-md p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                              aria-label="Delete coupon"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <AdminPagination
              currentPage={page}
              totalItems={filtered.length}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={setPerPage}
              itemLabel="coupons"
            />
          </div>
        </div>
      )}
    </div>
  );
}

